if (window.document.documentMode) {
  // if IE then try open same page in ms edge
  window.location = "microsoft-edge://" + document.location.href;
}
import { mapState, mapGetters } from "vuex";
import * as Sentry from "@sentry/vue";
import BootstrapVue from "bootstrap-vue";
import OptionsFieldSelect from "@/components/site/common/OptionsFieldSelect";
import TitleSelect from "@/components/site/common/TitleSelect";
import MobilePhoneInput from "@/components/site/common/MobilePhoneInput";
import AsyncSelect from "@/components/site/common/AsyncSelect";
import PrintRow from "@/components/site/common/PrintRow";
import PrintCol from "@/components/site/common/PrintCol";
import StaffName from "@/components/site/common/StaffName";
import CMSDate from "@/components/site/common/CMSDate";
import SelectDatepicker from "@/components/site/common/SelectDatepicker";
import WordsCounter from "@/components/site/common/WordsCounter";
import EntryGrade from "@/components/site/common/EntryGrade";
import GlobalErrors from "@/components/site/common/GlobalErrors";
import { formatSiteDate, formatSiteDateTime, yesNo, withArticle } from "@/filters";
import { getGenderDisplay, getGenderPronoun } from "@/features/student/services";
import { getAttendanceTypeName, formatMobilePhone } from "@/features/schools/services";
import { getOccupationalGroupNumber } from "@/features/parent/services";
import { reformatDOB } from "./time";
import HelpReferenceWrapper from "@/features/help-reference/HelpReferenceWrapper";
import EHQFormGroup from "@/components/site/common/EHQFormGroup";
import EHQFieldLabel from "@/components/site/common/EHQFieldLabel";
import GroupsOnly from "@/features/accounts/groups/GroupsOnly";
import { modal } from "./notify";

export const CMS = JSON.parse(document.getElementById("CMS_DATA").textContent);
export const SCHOOL_SETTINGS = JSON.parse(
  document.getElementById("SCHOOL_SETTINGS_DATA").textContent,
);
window.SchoolSettings = SCHOOL_SETTINGS;
window.CMS = CMS;

function setupSentry(app) {
  const ENROLHQ_SCRIPT_REGEX = /enrolHQInit/;
  const filterEventNotFromCustomScripts = (event) => {
    const getLastFrame = (event) => {
      const frames = event.exception.values[0].stacktrace.frames;
      return frames[frames.length - 1];
    };

    const logEvent = (event) => {
      const errorMsg = event.exception.values[0].value;
      const lastFrame = getLastFrame(event);
      // eslint-disable-next-line no-console
      console.error(errorMsg, `${lastFrame.filename}:${lastFrame.lineno}`);
    };

    // Different browsers describe the error slightly differently. Firefox does not hide the function even
    // if an error has been thrown from setTimeout. So, we have two branches for detecting custom script
    // errors: by regular expression and by comparing filename with window.location.href and function with "?".
    if (ENROLHQ_SCRIPT_REGEX.test(JSON.stringify(event))) {
      try {
        logEvent(event);
        return null;
      } catch {
        return null;
      }
    } else {
      // Some schools may use timeouts or put js code outside `enrolHQInit` at all,
      try {
        const lastFrame = getLastFrame(event);
        if (
          lastFrame &&
          lastFrame.function === "?" &&
          (lastFrame.filename === window.location.href ||
            // A frequent case when user redirected on 404 page
            // and delayed custom code executed.
            "/404/" === window.location.pathname)
        ) {
          logEvent(event);
          return null;
        }
      } catch {
        return null;
      }
    }

    return event;
  };

  const filterNotChunkLoadError = (event) => {
    // Ignore chunk load errors and reload browser to load new chunks.
    const message = event.exception.values[0].value;
    if (
      message &&
      (message.startsWith("Loading chunk") || message.startsWith("Loading CSS chunk"))
    ) {
      window.location.reload();
      return null;
    }
    return event;
  };

  Sentry.init({
    app,
    dsn: "https://efef0a4bbe0345598be85f64391aa034@sentry.enrolhq.com.au/11",
    ignoreErrors: [
      /window.mraid/, // reachlocal js
      /Can't find variable/, // this shouldn't happen in eslinted code
      /WebKit encountered an internal error/, // ios browser problem
      /Network Error/,
      /Request aborted/,
    ],
    denyUrls: [
      /\/capture_configs\//, // reachlocal js
      /\/maps[^/]*\/api\/js/, // ignore google maps errors
      /signature_pad/, // internal error in signature_pad
      /\/fbevents\.js/, // Facebook
      /\/gtm\.js/, // Google Tag Manager
      /\/gtag\/js/, // Google Tag Manager
      /analytics.tiktok.com\//,
      /cdn.taboola.com\//, // trc.taboola.com ads
    ],
    beforeSend(event) {
      return filterNotChunkLoadError(event) && filterEventNotFromCustomScripts(event);
    },
    normalizeDepth: 5,
  });
  window.sentryConfigureScope = (fn) => {
    Sentry.configureScope(fn);
  };

  if (window.SchoolSettings.api_version) {
    window.sentryConfigureScope(function (scope) {
      scope.setExtra("client-version", window.SchoolSettings.api_version);
      scope.setExtra("server-version", window.SchoolSettings.api_version);
    });
  }
  if (window.SchoolSettings.test_details) {
    Sentry.setTag("github_actor", window.SchoolSettings.test_details.github_actor);
    Sentry.setTag("github_head_ref", window.SchoolSettings.test_details.github_head_ref);
  }
}

export function setupComponents(config, app) {
  app.component("AsyncSelect", AsyncSelect);
  app.component("OptionsFieldSelect", OptionsFieldSelect);
  app.component("TitleSelect", TitleSelect);
  app.component("MobilePhoneInput", MobilePhoneInput);
  app.component("PrintRow", PrintRow); // TODO: remove component at all
  app.component("PrintCol", PrintCol); // TODO: remove component at all
  app.component("StaffName", StaffName);
  app.component("CmsDate", CMSDate);
  app.component("SelectDatepicker", SelectDatepicker);
  app.component("WordsCounter", WordsCounter);
  app.component("EntryGrade", EntryGrade);
  app.component("GlobalErrors", GlobalErrors);
  app.component("HelpReferenceWrapper", HelpReferenceWrapper);
  // eslint-disable-next-line vue/component-definition-name-casing
  app.component("b-form-group", EHQFormGroup);
  app.component("EHQFieldLabel", EHQFieldLabel);
  app.component("GroupsOnly", GroupsOnly);

  app.use(BootstrapVue);
  app.config.productionTip = false;
  app.mixin({
    computed: {
      ...mapState(["isPrint"]),
      ...mapGetters(["isEmbed"]),
    },
    beforeCreate() {
      this.CMS = CMS;
      this.SchoolSettings = SCHOOL_SETTINGS;
      if (process.env.VUE_APP_COMPAT) {
        this.$bvModal = modal;
      }
    },
  });

  // eslint-disable-next-line no-undef
  if (process.env.NODE_ENV === "production") {
    setupSentry(app);
  }

  config.$filters = {
    formatSiteDate: formatSiteDate,
    formatSiteDateTime: formatSiteDateTime,
    genderDisplay: getGenderDisplay,
    genderPronoun: getGenderPronoun,
    reformatDOB: reformatDOB,
    yesNo: yesNo,
    attendanceTypeName: getAttendanceTypeName,
    occupationalGroupNumber: getOccupationalGroupNumber,
    formatMobilePhone: formatMobilePhone,
    withArticle: withArticle,
  };
}
